import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const services = [
  {
    icon: FiIcons.FiFileText,
    title: 'Resume Writing & Optimization',
    description: 'Stand out from the crowd with a professionally crafted resume tailored to your target role and industry.',
    features: ['ATS-optimized formatting', 'Keyword-rich content', 'Tailored to your target industry', 'Multiple revision rounds'],
    color: 'from-blue-500 to-blue-700',
  },
  {
    icon: FiIcons.FiLinkedin,
    title: 'LinkedIn Profile Makeover',
    description: 'Transform your LinkedIn presence to attract recruiters, hiring managers, and professional opportunities.',
    features: ['Headline & summary rewrite', 'Experience section polish', 'Skills & endorsement strategy', 'Profile SEO optimization'],
    color: 'from-cyan-500 to-brand-accent',
  },
  {
    icon: FiIcons.FiEdit3,
    title: 'LinkedIn Content & Posts',
    description: 'Establish your thought leadership with compelling LinkedIn posts that grow your network and visibility.',
    features: ['Weekly post creation', 'Personal brand storytelling', 'Engagement-driven copy', 'Content calendar planning'],
    color: 'from-purple-500 to-purple-700',
  },
  {
    icon: FiIcons.FiTarget,
    title: 'Personal Brand Strategy',
    description: 'Define and communicate your unique professional value proposition across all platforms.',
    features: ['Brand voice development', 'Audience targeting', 'Competitive positioning', 'Long-term growth plan'],
    color: 'from-green-500 to-green-700',
  },
  {
    icon: FiIcons.FiMessageSquare,
    title: 'Career Messaging & Bio',
    description: 'Craft a powerful professional bio and messaging that clearly communicates who you are and what you offer.',
    features: ['Short & long-form bios', 'Elevator pitch writing', 'Email signature copy', 'Speaker bio creation'],
    color: 'from-orange-500 to-orange-700',
  },
  {
    icon: FiIcons.FiTrendingUp,
    title: 'Job Search Acceleration',
    description: 'Get a complete toolkit and strategy to land interviews faster and negotiate better offers.',
    features: ['Cover letter writing', 'Interview prep tips', 'Salary negotiation guidance', 'Application strategy'],
    color: 'from-red-500 to-red-700',
  },
];

const ServiceCard = ({ service, index }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.6 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="relative bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer border border-gray-100"
    >
      <motion.div
        className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}
      />
      <div className="p-7">
        <motion.div
          className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-5 shadow-md`}
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <SafeIcon icon={service.icon} className="text-white text-2xl" />
        </motion.div>
        <h3 className="text-xl font-bold text-brand-secondary mb-3">{service.title}</h3>
        <p className="text-gray-500 text-sm leading-relaxed mb-5">{service.description}</p>
        <AnimatePresence>
          <ul className="space-y-2">
            {service.features.map((feature, i) => (
              <motion.li
                key={i}
                className="flex items-center gap-2 text-sm text-gray-600"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + i * 0.07 }}
              >
                <SafeIcon icon={FiIcons.FiCheck} className="text-brand-accent flex-shrink-0" />
                {feature}
              </motion.li>
            ))}
          </ul>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const Services = () => (
  <section id="services" className="py-24 bg-white">
    <div className="container mx-auto px-6 max-w-7xl">
      <motion.div
        className="text-center mb-16"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <h2 className="text-sm font-bold text-brand-accent uppercase tracking-widest mb-2">What We Offer</h2>
        <h3 className="text-3xl md:text-5xl font-extrabold text-brand-secondary mb-4">
          Everything You Need to <span className="text-brand-accent">Land Your Next Role</span>
        </h3>
        <p className="text-gray-500 text-lg max-w-2xl mx-auto">
          From resume to LinkedIn to content strategy — we give career professionals the tools and presence to get noticed and get hired.
        </p>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {services.map((service, i) => (
          <ServiceCard key={i} service={service} index={i} />
        ))}
      </div>
    </div>
  </section>
);

export default Services;